import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  Loader2,
  Search,
  Plus,
  Edit,
  Trash2,
  Mail,
  User,
  Key,
  Link,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  MoreHorizontal,
  Send,
  LinkIcon,
  Unlink,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export const PortalAccountsAdmin = () => {
  const [portalUsers, setPortalUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Edit Dialog
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [editForm, setEditForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    customer_id: '',
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Create Dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
  });
  
  // Delete Dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  
  // Link Customer Dialog
  const [linkDialog, setLinkDialog] = useState({ open: false, user: null });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  
  // Password Reset Dialog
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, password: null, loading: false });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, customersRes] = await Promise.all([
        api.get('/admin/portal-users'),
        api.get('/customers'),
      ]);
      setPortalUsers(usersRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load portal users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (user) => {
    setEditForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      customer_id: user.customer_id || '',
      active: user.active !== false,
    });
    setEditDialog({ open: true, user });
    setShowPassword(false);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updateData = {
        email: editForm.email,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        customer_id: editForm.customer_id || null,
        active: editForm.active,
      };
      
      if (editForm.password.trim()) {
        updateData.password = editForm.password;
      }
      
      await api.put(`/admin/portal-users/${editDialog.user.id}`, updateData);
      toast.success('Portal account updated');
      setEditDialog({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password || !createForm.first_name || !createForm.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (createForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/portal-users', createForm);
      toast.success('Portal account created');
      setCreateDialog(false);
      setCreateForm({ email: '', first_name: '', last_name: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/portal-users/${deleteDialog.user.id}`);
      toast.success('Portal account deleted');
      setDeleteDialog({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleSendResetLink = async (user) => {
    try {
      await api.post(`/admin/portal-users/${user.id}/send-reset-link`);
      toast.success(`Setup link emailed to ${user.email} — valid for 7 days`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send setup link');
    }
  };

  const handleGeneratePassword = async (user) => {
    setResetDialog({ open: true, user, password: null, loading: true });
    setCopied(false);
    
    try {
      const response = await api.post(`/admin/portal-users/${user.id}/generate-password`, { send_email: true });
      setResetDialog({ 
        open: true, 
        user, 
        password: response.data.temporary_password,
        email: response.data.user_email,
        name: response.data.user_name,
        emailed: response.data.emailed,
        emailError: response.data.email_error,
        loading: false 
      });
      if (response.data.emailed) {
        toast.success(`Password reset and emailed to ${response.data.user_email}`);
      } else if (response.data.email_error) {
        toast.warning(`Password generated — email failed: ${response.data.email_error}. Share manually.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate password');
      setResetDialog({ open: false, user: null, password: null, loading: false });
    }
  };

  const copyPassword = () => {
    if (resetDialog.password) {
      navigator.clipboard.writeText(resetDialog.password);
      setCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleLinkCustomer = async () => {
    if (!selectedCustomerId) {
      toast.error('Please select a customer');
      return;
    }
    
    try {
      await api.post(`/admin/portal-users/${linkDialog.user.id}/link-customer/${selectedCustomerId}`);
      toast.success('Customer linked successfully');
      setLinkDialog({ open: false, user: null });
      setSelectedCustomerId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link customer');
    }
  };

  const handleUnlinkCustomer = async (user) => {
    try {
      // Send empty string to explicitly unlink customer
      await api.put(`/admin/portal-users/${user.id}`, { customer_id: "" });
      toast.success('Customer unlinked');
      fetchData();
    } catch (error) {
      toast.error('Failed to unlink customer');
    }
  };

  const filteredUsers = portalUsers.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      user.first_name.toLowerCase().includes(query) ||
      user.last_name.toLowerCase().includes(query) ||
      (user.customer_name && user.customer_name.toLowerCase().includes(query))
    );
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="portal-accounts-admin">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Customer Portal Accounts</h1>
            <p className="text-slate-500">Manage customer portal access and credentials</p>
          </div>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Account
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or linked customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Portal Users ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No portal users found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Linked Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.first_name} {user.last_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600">{user.email}</span>
                      </TableCell>
                      <TableCell>
                        {user.customer_name ? (
                          <div>
                            <p className="font-medium text-sm">{user.customer_name}</p>
                            <p className="text-xs text-slate-500">{user.customer_address}</p>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            Not Linked
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.active !== false ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">
                            <XCircle className="w-3 h-3 mr-1" />
                            Disabled
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.last_login_at ? (
                          <div className="text-sm" data-testid={`last-login-${user.id}`}>
                            <div className="text-slate-700">
                              {new Date(user.last_login_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                            <div className="text-xs text-slate-400">
                              {new Date(user.last_login_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                              {user.login_count > 1 ? ` · ${user.login_count}×` : ''}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-400 border-slate-200 text-xs" data-testid={`never-logged-in-${user.id}`}>
                            Never logged in
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">{formatDate(user.created_at)}</span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`actions-${user.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>Account Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleOpenEdit(user)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Password Reset</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => handleSendResetLink(user)}
                              className="flex-col items-start py-2"
                              data-testid={`send-reset-link-${user.id}`}
                            >
                              <div className="flex items-center w-full">
                                <Send className="w-4 h-4 mr-2 text-emerald-600" />
                                <span className="font-medium">Send Password Setup Link</span>
                              </div>
                              <span className="text-[11px] text-slate-500 ml-6">Recommended — customer picks their own password</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleGeneratePassword(user)}
                              className="flex-col items-start py-2"
                              data-testid={`generate-password-${user.id}`}
                            >
                              <div className="flex items-center w-full">
                                <RefreshCw className="w-4 h-4 mr-2 text-amber-600" />
                                <span className="font-medium">Generate Temporary Password</span>
                              </div>
                              <span className="text-[11px] text-slate-500 ml-6">Emails a temp password — useful for phone support</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Customer Link</DropdownMenuLabel>
                            {user.customer_id ? (
                              <DropdownMenuItem 
                                onClick={() => handleUnlinkCustomer(user)}
                                className="text-amber-600"
                              >
                                <Unlink className="w-4 h-4 mr-2" />
                                Unlink Customer
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => {
                                setLinkDialog({ open: true, user });
                                setSelectedCustomerId('');
                              }}>
                                <LinkIcon className="w-4 h-4 mr-2" />
                                Link to Customer
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteDialog({ open: true, user })}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Account
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, user: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Portal Account</DialogTitle>
              <DialogDescription>
                Update customer portal account details and credentials
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>New Password (leave blank to keep current)</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={editForm.password}
                    onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                    placeholder="Enter new password"
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Link to Customer</Label>
                <Select 
                  value={editForm.customer_id} 
                  onValueChange={(v) => setEditForm({...editForm, customer_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No linked customer</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.first_name} {customer.last_name} - {customer.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Account Active</p>
                  <p className="text-sm text-slate-500">Allow user to login to portal</p>
                </div>
                <Switch
                  checked={editForm.active}
                  onCheckedChange={(checked) => setEditForm({...editForm, active: checked})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, user: null })}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Portal Account</DialogTitle>
              <DialogDescription>
                Create a new customer portal account
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                    placeholder="Smith"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                    placeholder="customer@example.com"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-slate-500">If email matches a customer record, it will be automatically linked</p>
              </div>
              
              <div className="space-y-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                    placeholder="At least 6 characters"
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Portal Account</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the portal account for{' '}
                <span className="font-medium">
                  {deleteDialog.user?.first_name} {deleteDialog.user?.last_name}
                </span>
                ? This action cannot be undone. The customer will no longer be able to access the portal.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Link Customer Dialog */}
        <Dialog open={linkDialog.open} onOpenChange={(open) => setLinkDialog({ open, user: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5" />
                Link to Customer
              </DialogTitle>
              <DialogDescription>
                Link <span className="font-medium">{linkDialog.user?.first_name} {linkDialog.user?.last_name}</span> to a customer record
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Customer</Label>
                <Select 
                  value={selectedCustomerId} 
                  onValueChange={setSelectedCustomerId}
                >
                  <SelectTrigger data-testid="select-customer-link">
                    <SelectValue placeholder="Choose a customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        <div className="flex flex-col">
                          <span>{customer.first_name} {customer.last_name}</span>
                          <span className="text-xs text-slate-500">{customer.email}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Linking allows the portal user to view this customer's project details, documents, and payment information.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkDialog({ open: false, user: null })}>
                Cancel
              </Button>
              <Button onClick={handleLinkCustomer} disabled={!selectedCustomerId}>
                <LinkIcon className="w-4 h-4 mr-2" />
                Link Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog open={resetDialog.open} onOpenChange={(open) => {
          if (!open) setResetDialog({ open: false, user: null, password: null, loading: false });
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-600" />
                Password Reset
              </DialogTitle>
              <DialogDescription>
                {resetDialog.loading 
                  ? 'Generating a new password...'
                  : 'A new temporary password has been generated for this user.'
                }
              </DialogDescription>
            </DialogHeader>
            
            {resetDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              </div>
            ) : resetDialog.password ? (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{resetDialog.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span>{resetDialog.email}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>New Temporary Password</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        value={resetDialog.password}
                        readOnly
                        className="pr-10 font-mono text-lg bg-amber-50 border-amber-200"
                        data-testid="generated-password"
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={copyPassword}
                      className={copied ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className={`p-3 ${resetDialog.emailed ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'} border rounded-lg text-sm`}>
                  {resetDialog.emailed ? (
                    <>
                      <p className="font-medium mb-1 flex items-center gap-1.5">
                        <Check className="w-4 h-4" /> Email sent
                      </p>
                      <p className="text-emerald-700">
                        The customer has been emailed their new password and a portal sign-in link. Copy it above too in case you need to read it back to them over the phone.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium mb-1">
                        {resetDialog.emailError ? 'Email send failed — share manually' : 'Important'}
                      </p>
                      {resetDialog.emailError && (
                        <p className="text-xs mb-2 font-mono bg-amber-100 p-2 rounded">{resetDialog.emailError}</p>
                      )}
                      <ul className="list-disc list-inside space-y-1 text-amber-700">
                        <li>Share this password securely with the customer</li>
                        <li>Recommend they change it after first login</li>
                        <li>This password replaces their previous one</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            ) : null}
            
            <DialogFooter>
              <Button onClick={() => setResetDialog({ open: false, user: null, password: null, loading: false })}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
